import { RotateCcw, AlertTriangle } from "lucide-react";
import { useEffect } from "react";
import { DatePick, TimePick, iso, toDate, todayMt } from "@/components/adtemplate/DatePick";
import { getCloseCalendar } from "@/lib/pipelineApi";
import { autoStart } from "@/lib/mountainTime";
import { autoStartPreview } from "@/lib/applyApi";
import { AuctionClose } from "@/lib/brand";

export const START_HOURS = 72;
// Server-authoritative 72h start per close date (same tz data used when saving); browser calc only until it arrives.
const SRV = new Map();
export const fetchStart = (close) => {
  if (!close || SRV.has(close)) return Promise.resolve(SRV.get(close));
  return autoStartPreview(close, "20:00", START_HOURS).then((r) => { SRV.set(close, { date: r.date, time: r.time }); return SRV.get(close); }).catch(() => undefined);
};
const startFor = (close) => (close ? SRV.get(close) || autoStart(close, "20:00", START_HOURS) : null);
// Saved start that differs from the 72-hour rule = a manual override.
export const startOverride = (close, startDate, startTime) => {
  const a = startFor(close);
  return startDate && !(a && a.date === startDate && (!startTime || a.time === startTime)) ? { start: toDate(startDate), stime: startTime || "" } : { start: undefined, stime: "" };
};

const LBL = "block text-[11px] uppercase tracking-wider text-stone-400 font-semibold";

// Calendar-day arithmetic on the Edmonton date itself (no clock time involved, so DST can't shift it).
export const minusWeeks = (dateIso, w) => {
  const [y, m, d] = dateIso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d - 7 * w)).toISOString().slice(0, 10);
};

// v = { close: Date, post: Date|undefined (manual), start: Date|undefined (manual), stime: "HH:MM"|"" (manual), weeks: "" (scheduling override) }
export function computeDates(v, contractWeeks) {
  const weeks = contractWeeks || (/^\d+$/.test(String(v.weeks || "")) && +v.weeks >= 1 && +v.weeks <= 52 ? +v.weeks : null);
  const close = v.close ? iso(v.close) : null;
  const autoPost = close && weeks ? minusWeeks(close, weeks) : null;
  const post = v.post ? iso(v.post) : autoPost;
  // Bidding Starts = exactly 72 elapsed hours before the company's close (real timestamps, DST-safe).
  const auto = startFor(close);
  const startDate = v.start ? iso(v.start) : auto?.date || null;
  const startTime = v.stime || auto?.time || "";
  const startManual = !!(v.start || v.stime);
  const missing = !close || !startDate || !startTime;
  const bad = !missing && `${startDate}T${startTime}` >= `${close}T20:00`;
  const badPost = !!(post && close && post > close);
  const off72 = !!(auto && startManual && (startDate !== auto.date || startTime !== auto.time));
  const mismatch = !!(weeks && close && post !== autoPost);
  const badWeeks = !contractWeeks && v.weeks !== "" && v.weeks != null && !weeks;
  return { weeks, close, autoPost, post, auto, startDate, startTime, startManual, missing, bad, badPost, off72, mismatch, badWeeks, invalid: missing || bad || badPost || badWeeks };
}

const Mode = ({ manual, testId }) => <span data-testid={testId} className={`ml-1 normal-case tracking-normal font-semibold ${manual ? "text-amber-700" : "text-stone-400"}`}>{manual ? "(manually adjusted)" : "(calculated)"}</span>;
const Reset = ({ onClick, testId }) => <button type="button" data-testid={testId} onClick={onClick} className="inline-flex items-center gap-1 text-xs text-stone-700 underline"><RotateCcw className="h-3 w-3" />Use calculated date</button>;

export function AdDatesFields({ v, setV, contractWeeks, weeksSource, testPrefix, calendarMode }) {
  const d = computeDates(v, contractWeeks);
  const closeIso = v.close ? iso(v.close) : null;
  useEffect(() => { fetchStart(closeIso).then((r) => { if (r) setV((s) => ({ ...s })); }); }, [closeIso]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (x) => setV((s) => ({ ...s, [k]: x }));
  const t = (k) => `${testPrefix}-${k}`;
  return (
    <div data-testid={t("ad-dates")} className="space-y-2">
      <span className={LBL}>1. Scheduled Posting Date {(v.post || d.autoPost) && <Mode manual={!!v.post} testId={t("post-mode")} />}</span>
      <DatePick testId={t("post")} value={v.post || toDate(d.autoPost)} onChange={set("post")} placeholder={d.weeks ? "Pick Bidding Ends first" : "Set the advertising duration or pick a date"} />
      {v.post && <Reset testId={t("post-reset")} onClick={() => set("post")(undefined)} />}

      <span className={LBL}>2. Advertising Duration</span>
      {contractWeeks ? (
        <p data-testid={t("duration")} className="text-sm font-semibold text-stone-900">Advertising Duration: {contractWeeks} Week{contractWeeks === 1 ? "" : "s"} <span className="text-[11px] font-normal text-stone-500">({weeksSource})</span></p>
      ) : (
        <div className="space-y-1">
          <p data-testid={t("duration-not-set")} className="text-sm font-semibold text-amber-800">Advertising Duration Not Set</p>
          <label className="flex items-center gap-2 text-xs text-stone-600">Scheduling duration (weeks, doesn't change the contract)
            <input type="number" min={1} max={52} step={1} data-testid={t("weeks")} value={v.weeks ?? ""} onChange={(e) => set("weeks")(e.target.value)}
              className="h-10 w-20 rounded-md border border-stone-200 bg-white px-2 text-sm text-stone-900" />
          </label>
          {d.badWeeks && <p className="text-xs font-medium text-red-600">Enter a whole number from 1 to 52.</p>}
        </div>
      )}

      <span className={LBL}>3. Bidding Starts (72 hours before Bidding Ends) {d.startDate && <Mode manual={d.startManual} testId={t("start-mode")} />}</span>
      <div className="grid gap-2">
        <DatePick testId={t("start")} value={v.start || toDate(d.auto?.date)} onChange={set("start")} placeholder="Pick Bidding Ends first" />
        <TimePick testId={t("start-time")} value={d.startTime} onChange={set("stime")} />
      </div>
      {d.startManual && <Reset testId={t("start-reset")} onClick={() => setV((s) => ({ ...s, start: undefined, stime: "" }))} />}

      <span className={LBL}>4. Bidding Ends (<AuctionClose />)</span>
      <DatePick testId={t("close")} value={v.close} onChange={set("close")} placeholder="Pick the bidding end date" disabledBefore={todayMt()}
        marks={calendarMode ? () => getCloseCalendar(calendarMode) : undefined} />

      {d.missing && v.close && <p data-testid={t("missing")} className="text-xs font-medium text-amber-800">Select the Bidding Starts date and time.</p>}
      {d.bad && <p data-testid={t("order-error")} className="text-xs font-medium text-red-600">Bidding Starts must be before Bidding Ends (<AuctionClose />).</p>}
      {d.badPost && <p data-testid={t("post-error")} className="text-xs font-medium text-red-600">Scheduled Posting can't be after Bidding Ends.</p>}
      {d.off72 && <p data-testid={t("off72")} className="flex items-start gap-1 text-xs font-medium text-amber-800"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />Bidding Starts is no longer exactly 72 hours before Bidding Ends.</p>}
      {d.mismatch && <p data-testid={t("mismatch")} className="flex items-start gap-1 text-xs font-medium text-amber-800"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />The posting date doesn't match the {d.weeks}-week advertising duration. You can still save this override.</p>}
    </div>
  );
}
